import React from 'react';
import { motion } from 'framer-motion';

const Sports = () => {
  const achievements = [
    {
      sport: 'Football',
      icon: '⚽',
      level: 'Elite',
      description: 'Dandre is destined for greatness in football. His natural talent, dedication, and work ethic make him unstoppable on the field.',
      skills: ['Speed', 'Agility', 'Strategy', 'Leadership', 'Teamwork'],
      prediction: 'Future Professional Player',
      color: '#32CD32'
    },
    {
      sport: 'Basketball',
      icon: '🏀',
      level: 'Advanced',
      description: 'On the basketball court, Dandre dominates with his incredible skills and court vision. His steals are legendary and his work ethic is unmatched - always first to practice, last to leave.',
      skills: ['Steals', 'Work Ethic', 'Defense', 'Court Vision', 'Fast Breaks'],
      prediction: 'College Scholarship Material',
      color: '#FF8C00'
    },
    {
      sport: 'Athletics',
      icon: '🏃‍♂️',
      level: 'Elite',
      description: 'Dandre excels in track and field athletics, particularly in jumping events. His explosive power and technique make him a standout athlete.',
      skills: ['Long Jump', 'High Jump', 'Speed', 'Power', 'Technique'],
      prediction: 'Track & Field Champion',
      color: '#FFD700'
    }
  ];

  const stats = {
    football: {
      goals: 47,
      assists: 23,
      matches: 34,
      winRate: 85
    },
    basketball: {
      points: 312,
      rebounds: 89,
      assists: 67,
      games: 28
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="page-container">
      <motion.div 
        className="hero"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        style={{ padding: '2rem 0' }}
      >
        <h1>🏆 Sports Superstar</h1>
        <p>Dandre's journey to athletic greatness - from local legend to future champion</p>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid"
        style={{ marginTop: '2rem' }}
      >
        {achievements.map((achievement, index) => (
          <motion.div
            key={index}
            variants={itemVariants}
            className="card"
            style={{ 
              borderLeft: `4px solid ${achievement.color}`,
              position: 'relative',
              overflow: 'hidden'
            }}
            whileHover={{ 
              scale: 1.03,
              boxShadow: `0 20px 50px rgba(255, 255, 255, 0.1)`
            }}
          >
            <motion.div
              style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                fontSize: '8rem',
                opacity: 0.1,
                color: achievement.color
              }}
              animate={{ 
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {achievement.icon}
            </motion.div>
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <span style={{ fontSize: '3rem', marginRight: '1rem' }}>{achievement.icon}</span>
                <div>
                  <h2 style={{ margin: 0, color: achievement.color }}>{achievement.sport}</h2>
                  <p style={{ margin: 0, color: '#888', fontSize: '1rem' }}>Level: {achievement.level}</p>
                </div>
              </div>
              
              <p style={{ 
                fontSize: '1.1rem', 
                lineHeight: '1.6',
                color: '#ccc',
                marginBottom: '1.5rem'
              }}>
                {achievement.description}
              </p>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ color: achievement.color, marginBottom: '0.5rem' }}>Key Skills:</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {achievement.skills.map((skill, skillIndex) => (
                    <motion.span 
                      key={skillIndex}
                      style={{ 
                        background: achievement.color,
                        color: '#000',
                        padding: '0.3rem 0.8rem',
                        borderRadius: '15px',
                        fontSize: '0.9rem',
                        fontWeight: 'bold'
                      }}
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {skill}
                    </motion.span>
                  ))}
                </div>
              </div>
              
              <motion.div 
                style={{ 
                  background: `linear-gradient(135deg, ${achievement.color}20, ${achievement.color}10)`,
                  padding: '1rem',
                  borderRadius: '8px',
                  border: `1px solid ${achievement.color}40`,
                  textAlign: 'center'
                }}
                whileHover={{ 
                  background: `linear-gradient(135deg, ${achievement.color}30, ${achievement.color}20)`
                }}
              >
                <h4 style={{ color: achievement.color, margin: 0 }}>🔮 Future Prediction</h4>
                <p style={{ color: '#fff', margin: '0.5rem 0 0 0', fontWeight: 'bold' }}>
                  {achievement.prediction}
                </p>
              </motion.div>
            </div>
          </motion.div>
        ))}
      </motion.div>



      <motion.div 
        className="card"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.8 }}
        style={{ 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #111 0%, #222 100%)',
          border: '2px solid #333',
          marginTop: '2rem'
        }}
      >
        <h2 style={{ marginBottom: '2rem' }}>🌟 Why Dandre Will Make It Far</h2>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '2rem',
          marginBottom: '2rem'
        }}>
          <motion.div
            whileHover={{ scale: 1.05, rotateY: 5 }}
            style={{ 
              background: '#333', 
              padding: '2rem', 
              borderRadius: '15px',
              border: '1px solid #444'
            }}
          >
            <h3 style={{ color: '#FFD700', marginBottom: '1rem' }}>🎯 Natural Talent</h3>
            <p>Dandre has that rare combination of natural ability and athletic intelligence that can't be taught.</p>
          </motion.div>
          
          <motion.div
            whileHover={{ scale: 1.05, rotateY: 5 }}
            style={{ 
              background: '#333', 
              padding: '2rem', 
              borderRadius: '15px',
              border: '1px solid #444'
            }}
          >
            <h3 style={{ color: '#32CD32', marginBottom: '1rem' }}>💪 Work Ethic</h3>
            <p>His dedication to training and constant improvement sets him apart from the competition.</p>
          </motion.div>
          
          <motion.div
            whileHover={{ scale: 1.05, rotateY: 5 }}
            style={{ 
              background: '#333', 
              padding: '2rem', 
              borderRadius: '15px',
              border: '1px solid #444'
            }}
          >
            <h3 style={{ color: '#87CEEB', marginBottom: '1rem' }}>🧠 Mental Strength</h3>
            <p>Dandre has the mental toughness and resilience needed to succeed at the highest levels.</p>
          </motion.div>
        </div>
        
        <motion.div
          style={{ 
            background: 'linear-gradient(45deg, #FFD700, #FFA500)',
            color: '#000',
            padding: '2rem',
            borderRadius: '15px',
            marginTop: '2rem'
          }}
          animate={{ 
            boxShadow: [
              '0 0 20px rgba(255, 215, 0, 0.3)',
              '0 0 40px rgba(255, 215, 0, 0.6)',
              '0 0 20px rgba(255, 215, 0, 0.3)'
            ]
          }}
          transition={{ 
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <h2 style={{ margin: 0, marginBottom: '1rem' }}>🏆 Championship Prediction</h2>
          <p style={{ 
            fontSize: '1.3rem', 
            fontWeight: 'bold',
            margin: 0
          }}>
            Mark my words: Dandre is going to make it far in football. 
            His combination of skill, determination, and heart makes him unstoppable. 
            The sports world better get ready for this future champion! 🌟
          </p>
        </motion.div>
      </motion.div>

      <motion.div 
        className="card"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.0 }}
        style={{ 
          background: 'linear-gradient(45deg, #1a1a1a, #2a2a2a)',
          textAlign: 'center'
        }}
      >
        <h2>🎖️ Athletic Achievements Timeline</h2>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '1rem',
          marginTop: '2rem',
          textAlign: 'left'
        }}>
          <motion.div 
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              background: '#333',
              padding: '1rem',
              borderRadius: '10px',
              borderLeft: '4px solid #32CD32'
            }}
            whileHover={{ scale: 1.02 }}
          >
            <span style={{ fontSize: '2rem', marginRight: '1rem' }}>⚽</span>
            <div>
              <h4 style={{ color: '#32CD32', margin: 0 }}>Football Excellence</h4>
              <p style={{ color: '#ccc', margin: 0 }}>Dominating the field with incredible goals and assists</p>
            </div>
          </motion.div>
          
          <motion.div 
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              background: '#333',
              padding: '1rem',
              borderRadius: '10px',
              borderLeft: '4px solid #FF8C00'
            }}
            whileHover={{ scale: 1.02 }}
          >
            <span style={{ fontSize: '2rem', marginRight: '1rem' }}>🏀</span>
            <div>
              <h4 style={{ color: '#FF8C00', margin: 0 }}>Basketball Mastery</h4>
              <p style={{ color: '#ccc', margin: 0 }}>Showing incredible court vision and scoring ability</p>
            </div>
          </motion.div>
          
          <motion.div 
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              background: '#333',
              padding: '1rem',
              borderRadius: '10px',
              borderLeft: '4px solid #FFD700'
            }}
            whileHover={{ scale: 1.02 }}
          >
            <span style={{ fontSize: '2rem', marginRight: '1rem' }}>🏆</span>
            <div>
              <h4 style={{ color: '#FFD700', margin: 0 }}>Future Champion</h4>
              <p style={{ color: '#ccc', margin: 0 }}>Destined for greatness in professional sports</p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Sports;